import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { UntypedFormControl } from "@angular/forms";
import { MomentDateAdapter } from "@angular/material-moment-adapter";
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from "@angular/material/core";
import { MatDatepicker } from "@angular/material/datepicker";
import { select, Store } from "@ngrx/store";
// Depending on whether rollup is used, moment needs to be imported differently.
// Since Moment.js doesn't have a default export, we normally need to import using the `* as`
// syntax. However, rollup creates a synthetic default module and we thus need to import it using
// the `default as` syntax.
import * as _moment from "moment";
import { Subscription } from "rxjs";
import { InsurerDto } from "src/app/api/models";
import { TranslationService } from "src/app/core/services/translation.service";
import { DobTypeEnum } from "src/app/shared/enums/dobType.enum";
import { AppState, getState } from "src/app/state/app.state";
import { environment } from "src/environments/environment";
import { getFullAccess } from "../../authentication/state/authentication.actions";
import { getFullTokenLoaded } from "../../authentication/state/authentication.selectors";
import { loadContract } from "../../contract/state/contract.actions";
import { setTitle } from "../../title/state/title.actions";
import { setBirthdate, setCardIdShowPage } from "../../transaction-info/state/transaction-info.actions";
import { getBirthdateIsValid, getLoginError } from "../../transaction-info/state/transaction-info.selectors";
import { InsurerService } from 'src/app/api/services';


const moment = _moment;

// See the Moment.js docs for the meaning of these formats:
// https://momentjs.com/docs/#/displaying/format/
export const MY_FORMATS = {
	parse: {
		dateInput: "DD/MM/YYYY",
	},
	display: {
		dateInput: "DD/MM/YYYY",
		monthYearLabel: "YYYY",
		dateA11yLabel: "LL",
		monthYearA11yLabel: "YYYY",
	},
};

@Component({
    selector: "app-patient-dob",
    templateUrl: "./patient-dob.component.html",
    styleUrls: ["./patient-dob.component.scss"],
    providers: [
        { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE] },
        { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
    ],
    standalone: false
})
export class PatientDobComponent implements OnInit,OnDestroy {
	@ViewChild("formDateEl") formDateEl: ElementRef;

	public formDate = new UntypedFormControl("");

	private insurer: InsurerDto = undefined;
	private cardId: string = undefined;
  private previousDateInput = '';
	public formLabel: string;
	public formPlaceholder: string;
	public loginError: string = undefined;

	public dobType: DobTypeEnum;
	public formStrlen: number;
	public startDate = new Date(1990, 0, 1);
	private subs: Subscription[] = [];
	private state: AppState;
	constructor(private store: Store<AppState>, private _adapter: DateAdapter<any>, private translationService: TranslationService) {
		this.subs.push(getState(this.store).subscribe(state =>{
			this.state = state;
			this._adapter.setLocale(state.transactionInfo.selectedLanguage.languageCode.toLowerCase());
			this.insurer = state.transactionInfo.selectedInsurer;
			this.cardId = state.transactionInfo.cardId;
			this.dobType = DobTypeEnum[state.transactionInfo.selectedInsurerDobType];
		}));
	}

	ngOnInit(): void {
		//If the dob is not need => this call with fire the change of component via ViewLocator
		if (this.dobType === DobTypeEnum.NOACTION) {
			//We get full access + load contract
			this.store.dispatch(setBirthdate({ data: "" }));
			this.store.dispatch(getFullAccess({ insurerId: this.insurer.insurerId, cardId: this.cardId, dob: null }));
			this.getContract();
		} else {
			this.formLabel = this.dobType === DobTypeEnum.ASKYEAR ? "LABEL_ASKYEAR" : "LABEL_ASKDATE";
			this.formLabel = this.translationService.getInstant(this.formLabel);
			this.formPlaceholder = this.dobType === DobTypeEnum.ASKYEAR ? "YYYY" : "DD/MM/YYYY";
			this.formStrlen = this.dobType === DobTypeEnum.ASKYEAR ? 4 : 10;
		}
		this.store.dispatch(
			setTitle({
				data: "TITLE_PATIENT_DOB",
				data2: "TITLE_PATIENT_DOB_INFO",
			})
		);
		this.subs.push(
			this.store
				.pipe(select(getBirthdateIsValid))
				.subscribe((dobValid) => {
					if (dobValid === false) {
						this.formDate.setValue("");
						this.formDate.setErrors({ dobInvalidSubmit: true });
						this.formDate.markAsTouched();
						this.formDateEl.nativeElement.focus();
					}
				})
		);
		this.subs.push(
			this.store.pipe(select(getLoginError)).subscribe((error) => {
				if (error) {
					this.loginError = error;
					this.formDate.setErrors({ dobInvalidSubmit: true });
				} else {
					this.formDate.setErrors({ dobInvalidSubmit: false });
				}
			})
		);

		if (environment.kioskMode && window.external.ShowKeyboardNumeric) window.external.ShowKeyboardNumeric();
	}

	ngOnDestroy(): void {
		this.subs.forEach((_) => _.unsubscribe());
	}

	public onClickRetour() {
		this.store.dispatch(setCardIdShowPage({ data: true }));
		this.store.dispatch(setBirthdate({ data: undefined }));
	}

	public onClickOk() {
		if (this.dobType === DobTypeEnum.ASKDATE) {
			const date = new Date(this.formDate.value);
			if (!this.formDate.value.toString().match(/^[0-9]{2}\/[0-9]{2}\/[0-9]{4}$/) || date == null || date.getFullYear() < new Date().getFullYear() - 150) {
				this.formDate.setErrors({ invalid: true });

			}
		}
		if (this.dobType === DobTypeEnum.ASKYEAR) {
			if (!this.formDate.value.toString().match(/^[0-9]{4}$/) || isNaN(Number(this.formDate.value)) || Number(this.formDate.value) < new Date().getFullYear() - 150) {
				this.formDate.setErrors({ invalid: true });
			}
		}

		if (this.formDate.invalid || this.formDate.value.toString().trim() === "") return;
		
		this.store.dispatch(setBirthdate({ data: this.formDate.value.toString() }));
		this.store.dispatch(getFullAccess({ insurerId: this.insurer.insurerId, cardId: this.cardId, dob: this.formDate.value.toString() }));

		this.getContract();
	}

	private getContract() {
		this.subs.push(
			this.store.pipe(select(getFullTokenLoaded)).subscribe((fullTokenLoaded) => {
				if (fullTokenLoaded !== true)
					return;

				this.store.dispatch(loadContract({
					InsurerId: this.state.transactionInfo.selectedInsurer?.insurerId,
					CardNumber: this.cardId, SiteId: this.state.transactionInfo.defaultSiteId,
					Language: this.state.transactionInfo.selectedLanguage,
					InsurerQuestionnaireId: this.state.transactionInfo.selectedInsurer?.questionnaireId 
				}));
			})
		);
	}

	public change(event) {
		if (this.dobType === DobTypeEnum.ASKDATE && event.key === "/") {
			this.format(this.formDate.value.toString() + "/");
			event.preventDefault();
		}
	}

	public format(str: string) {
		if (this.dobType === DobTypeEnum.ASKDATE) {
			str = str.replace(/^([0-9])\//, "0$1/");
			str = str.replace(/\/([0-9])\//, "/0$1/");
			this.formDate.setValue(str);
		}
	}

	public onPickerDateChange(event) {
		if (this.dobType === DobTypeEnum.ASKYEAR) {
			this.formDate.setValue(event.value.format("YYYY"));
		} else {
			this.formDate.setValue(event.value.format("DD/MM/YYYY"));
		}
	}

	public onChosenYearHandler(normalizedYear: _moment.Moment, datepicker: MatDatepicker<_moment.Moment>) {
		if (this.dobType === DobTypeEnum.ASKYEAR) {
			datepicker.close();
			this.formDate.setValue(normalizedYear.year());
		}
	}
  
  onDateInput(event: any): void {
    const input = event.target;
    let value = input.value.replace(/\D/g, '');
    const cursorPosition = input.selectionStart;
    const previousLength = this.formDate.value?.length || 0;

    const isDeleting = this.previousDateInput.length > input.value.length;
    
    if (value.length > 8) {
      value = value.substring(0, 8);
    }
  
    if (value.length >= 1) {
      const firstDigit = parseInt(value[0]);
      if (firstDigit > 3) {
        value = '3' + value.substring(1);
      }
    }
    
    if (value.length >= 2) {
      const day = parseInt(value.substring(0, 2));
      if (day > 31) {
        value = '31' + value.substring(2);
      }
      else if (day === 0) {
        value = '01' + value.substring(2);
      }
    }
    
    if (value.length >= 3) {
      const firstMonthDigit = parseInt(value[2]);
      if (firstMonthDigit > 1) {
        value = value.substring(0, 2) + '1' + value.substring(3);
      }
    }
    
    if (value.length >= 4) {
      const month = parseInt(value.substring(2, 4));
      if (month > 12) {
        value = value.substring(0, 2) + '12' + value.substring(4);
      }
      else if (month === 0) {
        value = value.substring(0, 2) + '01' + value.substring(4);
      }
    }
    
    let formatted = '';
    if (value.length >= 1) {
      formatted = value.substring(0, 2);
    }
    if (value.length >= 2) {
      formatted += '/' + value.substring(2, 4);
    }
    if (value.length >= 4) {
      formatted += '/' + value.substring(4, 8);
    }

    if (isDeleting && this.previousDateInput.endsWith('/')) {
      formatted = formatted.substring(0, formatted.length - 2);
    }
    
    this.formDate.setValue(formatted, { emitEvent: false });
    this.previousDateInput = formatted;
    
    setTimeout(() => {
      let newCursorPos = cursorPosition;
      
      if (formatted.length > previousLength && 
          (formatted[cursorPosition] === '/' || formatted[cursorPosition - 1] === '/')) {
        newCursorPos = cursorPosition + 1;
      }
      
      input.selectionStart = input.selectionEnd = Math.min(newCursorPos, formatted.length);
    }, 0);
  }
}
